import { useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  FormControlLabel,
  Checkbox,
  TextField,
  InputAdornment,
  IconButton,
  useTheme,
  Box,
  Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import InfoIcon from '@mui/icons-material/Info';
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import openDatePicker from '../common/util/openDatePicker';
import { useLocalization } from '../common/components/LocalizationProvider';
import { MuiFileInput } from 'mui-file-input';
import EditItemView from './components/EditItemView';
import EditAttributesAccordion from './components/EditAttributesAccordion';
import SelectField from '../common/components/SelectField';
import deviceCategories from '../common/util/deviceCategories';
import { useTranslation } from '../common/components/LocalizationProvider';
import useDeviceAttributes from '../common/attributes/useDeviceAttributes';
import SettingsMenu from './components/SettingsMenu';
import useCommonDeviceAttributes from '../common/attributes/useCommonDeviceAttributes';
import { useCatch } from '../reactHelper';
import useSettingsStyles from './common/useSettingsStyles';
// localization handled inside LocalizedDateField
import QrCodeDialog from '../common/components/QrCodeDialog';
// useSelector not required in this file currently
import { useAdministrator, useManager } from '../common/util/permissions';
import fetchOrThrow from '../common/util/fetchOrThrow';

const DevicePage = () => {
  const { classes } = useSettingsStyles();
  const t = useTranslation();
  // const theme = useTheme();

  const expirationRef = useRef(null);
  const { htmlLang } = useLocalization();
  const theme = useTheme();

  const isAdmin = useAdministrator();
  const isManager = useManager();
  const isAdminOrManager = isAdmin || isManager;

  // selectors kept out when not used to avoid lint warnings

  const commonDeviceAttributes = useCommonDeviceAttributes(t);
  const deviceAttributes = useDeviceAttributes(t);

  const [searchParams] = useSearchParams();
  const uniqueId = searchParams.get('uniqueId');

  const ensureFormStructure = (currentItem) => {
    if (!currentItem) return currentItem;

    return {
      ...currentItem,
      attributes: {
        ...currentItem.attributes,
        form: currentItem.attributes?.form || {}
      }
    };
  };

  const [item, setItem] = useState(null);
  const handleSetItem = (newItem) => {
    setItem(ensureFormStructure(newItem));
  };

  const [showQr, setShowQr] = useState(false);
  const [imageFile, setImageFile] = useState(null);

  const handleFileInput = useCatch(async (newFile) => {
    setImageFile(newFile);
    if (newFile && item?.id) {
      const response = await fetchOrThrow(`/api/devices/${item.id}/image`, {
        method: 'POST',
        body: newFile,
      });
      setItem({ ...item, attributes: { ...item.attributes, deviceImage: await response.text() } });
    } else if (!newFile) {
      const remainingAttributes = { ...(item.attributes || {}) };
      if ('deviceImage' in remainingAttributes) delete remainingAttributes.deviceImage;
      setItem((prev) => ensureFormStructure({ ...prev, attributes: remainingAttributes }));
    }
  });

  const updateFormField = (key, value) => {
    setItem((prev) => ensureFormStructure({
      ...prev,
      attributes: {
        ...prev.attributes,
        form: {
          ...prev.attributes?.form,
          [key]: value
        }
      }
    }));
  };

  const validate = () => item && item.name && item.uniqueId;

  const filterAttributes = (attributes) => {
    if (!attributes) return {};

    // Preservar campos do form
    const formFields = attributes.form || {};

    const filteredAttributes = {form: {...formFields}};

    Object.keys(attributes).forEach(key => {
      if (key === 'form') return;

      const value = attributes[key];
      if (value !== null && value !== undefined && typeof value !== 'object') {
        filteredAttributes[key] = value;
      }
    });

    return filteredAttributes;
  };

  const updateAttributes = (newAttributes) => {
    const updatedAttributes = { ...newAttributes };
    if (item.attributes) {
      if (item.attributes.form) updatedAttributes.form = item.attributes.form;
    }
    setItem({ ...item, attributes: updatedAttributes });
  };

  return (
    <EditItemView
      endpoint="devices"
      item={item}
      setItem={handleSetItem}
      validate={validate}
      menu={<SettingsMenu />}
      breadcrumbs={['settingsTitle', 'sharedDevice']}
    >
      {item && (
        <>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" gap={1}>
                <NewReleasesIcon color="primary" />
                <Typography variant="subtitle1">{t('sharedRequired')}</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails className={classes.details}>
              <Box
                display="flex"
                flexDirection="column"
                gap={2}
                width="100%"
              >
                {/* Primeira linha - Campos principais */}
                <Box
                  display="flex"
                  width="100%"
                  gap={2}
                  sx={{ flexDirection: { xs: 'column', sm: 'row' } }}
                >
                  {/* Grupo */}
                  <Box flex={1} minWidth={0}>
                    <SelectField
                      fullWidth
                      value={item.groupId}
                      onChange={(event) => setItem({ ...item, groupId: Number(event.target.value) })}
                      endpoint="/api/groups"
                      label={t('groupParent')}
                      disabled={!isAdminOrManager}
                    />
                  </Box>

                  {/* Nome */}
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      value={item.name || ''}
                      onChange={(event) => setItem({ ...item, name: event.target.value })}
                      label={t('sharedName')}
                      disabled={!isAdminOrManager}
                    />
                  </Box>

                  {/* ID Único */}
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      value={item.uniqueId || ''}
                      onChange={(event) => setItem({ ...item, uniqueId: event.target.value })}
                      label={t('deviceIdentifier')}
                      //helperText={t('deviceIdentifierHelp')}
                      disabled={!isAdminOrManager || Boolean(uniqueId)}
                    />
                  </Box>
                </Box>

                {/* Segunda linha - Placa e Contato */}
                <Box
                  display="flex"
                  width="100%"
                  gap={2}
                  sx={{ flexDirection: { xs: 'column', sm: 'row' } }}
                >
                  {/* Placa */}
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      value={item?.attributes?.form?.plate || ''}
                      onChange={(e) => updateFormField('plate', e.target.value)}
                      label={t("sharedDevicePlate")}
                      disabled={!isAdminOrManager}
                    />
                  </Box>

                  {/* Contato */}
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      value={item.contact || ''}
                      onChange={(event) => setItem({ ...item, contact: event.target.value })}
                      label={t('deviceContact')}
                      disabled={!isAdminOrManager}
                    />
                  </Box>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" gap={1}>
                <InfoIcon color="primary" /> {/* Adicione esta linha */}
                <Typography variant="subtitle1">
                  {t('sharedExtra')}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails className={classes.details}>
              <Box display="flex" flexDirection="column" gap={3}>
                {/* Linha 1 - Dados básicos do veículo */}
                <Box display="flex" gap={2} sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      value={item?.attributes?.form?.brand || ''}
                      onChange={(e) => updateFormField('brand', e.target.value)}
                      label={t("deviceBrand")}
                      disabled={!isAdminOrManager}
                    />
                  </Box>
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      value={item?.attributes?.form?.model || ''}
                      onChange={(e) => updateFormField('model', e.target.value)}
                      label={t("deviceModel")}
                      disabled={!isAdminOrManager}
                    />
                  </Box>
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      value={item?.attributes?.form?.color || ''}
                      onChange={(e) => updateFormField('color', e.target.value)}
                      label={t("deviceColor")}
                      disabled={!isAdminOrManager}
                    />
                  </Box>
                </Box>

                {/* Linha 2 - Informações técnicas */}
                <Box display="flex" gap={2} sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      value={item?.attributes?.form?.fuelConsumption || ''}
                      onChange={(e) => updateFormField('fuelConsumption', e.target.value)}
                      label={t("deviceFuelconsumption")}
                      disabled={!isAdminOrManager}
                    />
                  </Box>
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      value={item.phone || ''}
                      onChange={(e) => setItem({ ...item, phone: e.target.value })}
                      label={t('sharedPhone')}
                      disabled={!isAdminOrManager}
                    />
                  </Box>
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      value={item.model || ''}
                      onChange={(e) => setItem({ ...item, model: e.target.value })}
                      label={t('sharedEquipmentModel')}
                      disabled={!isAdminOrManager}
                    />
                  </Box>
                </Box>

                {/* Linha 3 - Informações do chip */}
                <Box display="flex" gap={2} sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      value={item?.attributes?.form?.operator || ''}
                      onChange={(e) => updateFormField('operator', e.target.value)}
                      label={t("sharedOperatorChip")}
                      disabled={!isAdminOrManager}
                    />
                  </Box>
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      value={item?.attributes?.form?.vendor || ''}
                      onChange={(e) => updateFormField('vendor', e.target.value)}
                      label={t("sharedVendorChip")}
                      disabled={!isAdminOrManager}
                    />
                  </Box>
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      value={item?.attributes?.form?.iccid || ''}
                      onChange={(e) => updateFormField('iccid', e.target.value)}
                      label={t("sharedIMEIChip")}
                      disabled={!isAdminOrManager}
                    />
                  </Box>
                </Box>

                {/* Linha 4 - Documentações */}
                <Box display="flex" gap={2} sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      value={item?.attributes?.form?.prefix || ''}
                      onChange={(e) => updateFormField('prefix', e.target.value)}
                      label={t("sharedPrefixo")}
                      disabled={!isAdminOrManager}
                    />
                  </Box>
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      value={item?.attributes?.form?.chassi || ''}
                      onChange={(e) => updateFormField('chassi', e.target.value)}
                      label={t("sharedChassi")}
                      disabled={!isAdminOrManager}
                    />
                  </Box>
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      value={item?.attributes?.form?.vin || ''}
                      onChange={(e) => updateFormField('vin', e.target.value)}
                      label={t("sharedRenavam")}
                      disabled={!isAdminOrManager}
                    />
                  </Box>
                </Box>

                {/* Linha 5 - Configurações adicionais */}
                <Box display="flex" flexDirection="column" gap={2}>
                  <Box display="flex" gap={2} sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
                    <Box flex={1} minWidth={0}>
                      <SelectField
                        fullWidth
                        value={item.category || 'default'}
                        onChange={(e) => setItem({ ...item, category: e.target.value })}
                        data={deviceCategories.map((category) => ({
                          id: category,
                          name: t(`category${category.replace(/^\w/, (c) => c.toUpperCase())}`),
                        })).filter(a => a.name).sort((a, b) => a.name.localeCompare(b.name))}
                        label={t('deviceCategory')}
                        disabled={!isAdminOrManager}
                      />
                    </Box>
                    <Box flex={1} minWidth={0}>
                      <SelectField
                        fullWidth
                        value={item.calendarId}
                        onChange={(e) => setItem({ ...item, calendarId: Number(e.target.value) })}
                        endpoint="/api/calendars"
                        label={t('sharedCalendar')}
                        disabled={!isAdminOrManager}
                      />
                    </Box>
                  </Box>

                  <Box display="flex" gap={2} sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
                    <Box flex={1} minWidth={0}>
                      {/* Campo de data com input nativo e ícone que abre o picker */}
                      <TextField
                        fullWidth
                        value={item.expirationTime ? new Date(item.expirationTime).toISOString().slice(0,10) : ''}
                        onChange={(e) => {
                          if (e.target.value) {
                            setItem({ ...item, expirationTime: new Date(e.target.value).toISOString() });
                          } else {
                            setItem({ ...item, expirationTime: null });
                          }
                        }}
                        label={t('userExpirationTime')}
                        disabled={!isAdminOrManager}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          inputProps: { type: 'date', lang: htmlLang },
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton size="small" edge="end" onClick={() => openDatePicker(expirationRef.current)}>
                                <CalendarTodayIcon sx={{ color: theme.palette.action.active }} />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        inputRef={expirationRef}
                        className={classes.dateInput}
                      />
                    </Box>
                    <Box flex={1} minWidth={0} display="flex" alignItems="center">
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={item.disabled}
                            onChange={(e) => setItem({ ...item, disabled: e.target.checked })}
                          />
                        }
                        label={t('sharedDisabled')}
                        disabled={!isAdminOrManager}
                      />
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => setShowQr(true)}
                        disabled={!isAdminOrManager}
                      >
                        {t('sharedQrCode')}
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          {item.id && (
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" gap={1}>
                <AddAPhotoIcon color="primary" />
                <Typography variant="subtitle1">
                  {t('attributeDeviceImage')}
                </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails className={classes.details}>
                <MuiFileInput
                  placeholder={t('attributeDeviceImage')}
                  value={imageFile}
                  onChange={handleFileInput}
                  inputProps={{ accept: 'image/*' }}
                  disabled={!isAdminOrManager}
                />
              </AccordionDetails>
            </Accordion>
          )}
          <EditAttributesAccordion
            attributes={filterAttributes(item.attributes)}
            setAttributes={updateAttributes}
            definitions={{ ...commonDeviceAttributes, ...deviceAttributes }}
            disabled={!isAdminOrManager}
          />
        </>
      )}
    <QrCodeDialog open={showQr} onClose={() => setShowQr(false)} />
    </EditItemView>
  );
};

export default DevicePage;
